import type {
    CognitoIDTokenPayload,
    User
} from '@auth/types';

const decodeJWTPayload = (token: string): unknown => {
    if (!token) {
        return undefined;
    }

    const tokenParts = token.split('.');

    if (tokenParts.length < 2) {
        return undefined;
    }

    try {
        const normalizedPayload = tokenParts[1].replace(/-/g, '+').replace(/_/g, '/');

        const paddedPayload = normalizedPayload.padEnd(Math.ceil(normalizedPayload.length / 4) * 4, '=');

        return JSON.parse(window.atob(paddedPayload));
    } catch (error) {
        return undefined;
    }
};

const isCognitoIDTokenPayload = (payload: unknown): payload is CognitoIDTokenPayload => {
    if (!payload || typeof payload !== 'object') {
        return false;
    }

    const candidate = payload as Partial<CognitoIDTokenPayload>;
    const rolesList = candidate['cognito:groups'];

    return typeof candidate.aud === 'string'
        && candidate.aud === process.env.COGNITO_CLIENT_ID
        && typeof candidate.auth_time === 'number'
        && typeof candidate.email === 'string'
        && (candidate.email_verified === undefined || typeof candidate.email_verified === 'boolean')
        && typeof candidate.exp === 'number'
        && (candidate.family_name === undefined || typeof candidate.family_name === 'string')
        && (candidate.given_name === undefined || typeof candidate.given_name === 'string')
        && typeof candidate.iat === 'number'
        && typeof candidate.iss === 'string'
        && candidate.iss === process.env.COGNITO_AUTHORITY
        && typeof candidate.sub === 'string'
        && candidate.token_use === 'id'
        && (rolesList === undefined || (
            Array.isArray(rolesList)
            && rolesList.every((role) => typeof role === 'string')
        ));
};

const buildAppAuthUser = (token?: string): User | undefined => {
    if (!token) {
        return undefined;
    }

    const payload = decodeJWTPayload(token);

    if (!isCognitoIDTokenPayload(payload)) {
        return undefined;
    }

    const {
        'cognito:groups': rolesList = [],
        email: emailAddress,
        email_verified: isEmailVerified,
        family_name: lastName,
        given_name: firstName,
        sub: userID
    } = payload;

    return ({
        actorID: userID,
        emailAddress,
        firstName,
        isEmailVerified: !!isEmailVerified,
        lastName,
        rolesList: [
            ...rolesList
        ]
    });
};

export default buildAppAuthUser;
